import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  User,
  ShoppingBag,
  Coins,
  QrCode,
  MapPin,
  History,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronLeft,
  ChevronRight,
  DollarSign
} from 'lucide-react';
import CustomerQRCode from './CustomerQRCode';
import DataTable from '../common/DataTable';
import { AllProfile, AllCustomerShopping, fetchNearbyShops, createClaimRequest, AllProfileUpdate, createCustomerTransaction, updateCustomerTransaction } from '../../services/api';
import { toast } from 'react-hot-toast';
import FindMyShop from './FindMyShop';
import TransactionHistory from './TransactionHistory';

const CustomerDashboard = () => {
  const { currentUser, logout } = useAuth();
  const { theme } = useTheme();

  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalPurchases: 0,
    totalPoints: 0,
    amountSpent: 0,
    amountSaved: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', mobile: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  // Claim Points modal state
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimShopName, setClaimShopName] = useState('');
  const [claimBusinessEmail, setClaimBusinessEmail] = useState('');
  const [claimAmount, setClaimAmount] = useState('');
  const [claimBillFile, setClaimBillFile] = useState(null);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  // Auto-search state for shop/email lookup
  const [claimOptions, setClaimOptions] = useState([]); // {shopName, email}
  const [claimOptionsVisible, setClaimOptionsVisible] = useState(false);
  const [claimOptionsLoading, setClaimOptionsLoading] = useState(false);

  // Redeem via Claim Points toggle
  const [usePoints, setUsePoints] = useState(false);

  // Prefetch shops when opening claim modal (now inside component)
  useEffect(() => {
    const load = async () => {
      try {
        setClaimOptionsLoading(true);
        const resp = await fetchNearbyShops({});
        const profiles = Array.isArray(resp?.extractedData) ? resp.extractedData : (resp?.extractedData?.records || []);
        const opts = (profiles || []).map((p) => {
          const shopArr = Array.isArray(p.shopDetails) ? p.shopDetails : [];
          const shopName = shopArr?.[0]?.shopName || '';
          return { shopName, email: p.email || '' };
        }).filter(o => o.shopName || o.email);
        setClaimOptions(opts);
      } catch (e) {
        // silent
      } finally {
        setClaimOptionsLoading(false);
      }
    };
    if (showClaimModal) load();
  }, [showClaimModal]);

  const getFilteredClaimOptions = () => {
    const q = (claimShopName || claimBusinessEmail || '').toLowerCase().trim();
    if (!q) return claimOptions.slice(0, 8);
    return claimOptions
      .filter(o => (o.shopName || '').toLowerCase().includes(q) || (o.email || '').toLowerCase().includes(q))
      .slice(0, 8);
  };

  const applyClaimSelection = (opt) => {
    setClaimShopName(opt.shopName || '');
    setClaimBusinessEmail(opt.email || '');
    setClaimOptionsVisible(false);
  };

  const [currentView, setCurrentView] = useState('dashboard'); 
  // possible values: 'dashboard', 'cardDetails', 'findShops', 'nearbyShops', 'transactions', 'qrCode', 'allActivity'
  const [selectedCard, setSelectedCard] = useState(null);
  const [tableData, setTableData] = useState([]);
  // Card details filters & pagination
  const [cardDateFilter, setCardDateFilter] = useState('all'); // all | today | lastWeek | lastMonth | custom
  const [cardCustomDateRange, setCardCustomDateRange] = useState({ start: '', end: '' });
  const [cardCurrentPage, setCardCurrentPage] = useState(1);
  const [cardItemsPerPage] = useState(10);
  const [cardSearchTerm, setCardSearchTerm] = useState('');
  const [cardExactDate, setCardExactDate] = useState('');
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [allActivityCurrentPage, setAllActivityCurrentPage] = useState(1);
  const [allActivityItemsPerPage] = useState(10);
  // All Activity page filters
  const [allSearchTerm, setAllSearchTerm] = useState('');
  const [allAmountMin, setAllAmountMin] = useState('');
  const [allAmountMax, setAllAmountMax] = useState('');
  const [allDateFilter, setAllDateFilter] = useState('all'); // all | today | thisMonth | custom
  const [allCustomDateRange, setAllCustomDateRange] = useState({ start: '', end: '' });
  const [allExactDate, setAllExactDate] = useState('');
  // Recent Activity (on dashboard) filters & pagination
  const [recentSearchTerm, setRecentSearchTerm] = useState('');
  const [recentDateFilter, setRecentDateFilter] = useState('all'); // all | today | thisMonth | custom
  const [recentCustomDateRange, setRecentCustomDateRange] = useState({ start: '', end: '' });
  const [recentCurrentPage, setRecentCurrentPage] = useState(1);
  const [recentItemsPerPage] = useState(10);
  // Exact date picker for Recent Activity (search-right-date)
  const [recentExactDate, setRecentExactDate] = useState('');

  useEffect(() => {
    fetchUserStats();
  }, [currentUser]);

  // Open profile modal automatically if role is customer and profile incomplete
  useEffect(() => {
    if (!currentUser) return;
    const role = sessionStorage.getItem('selectedRole') || localStorage.getItem('selectedRole');
    if (role !== 'customer') return;
    // check basic completeness after stats fetch
    (async () => {
      try {
        const res = await AllProfile({});
        const list = Array.isArray(res.extractedData) ? res.extractedData : (res.extractedData?.records || []);
        const mine = list.find(p => (p.profile_id === currentUser.uid) || (p.email === currentUser.email));
        if (!mine || !mine.firstName || !mine.mobile) {
          setProfileForm({
            firstName: mine?.firstName || currentUser?.displayName?.split(' ')[0] || '',
            lastName: mine?.lastName || currentUser?.displayName?.split(' ')[1] || '',
            mobile: mine?.mobile || ''
          });
          setShowProfileModal(true);
        }
      } catch (e) {
        // silent
      }
    })();
  }, [currentUser]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdownMenu && !event.target.closest('.dropdown-container')) {
        setShowDropdownMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdownMenu]);

  const fetchUserStats = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);

      const profileResponse = await AllProfile({});
      const shoppingResponse = await AllCustomerShopping({});

      const profileData =
        profileResponse?.extractedData || profileResponse?.data || profileResponse || [];
      const shoppingData =
        shoppingResponse?.extractedData || shoppingResponse?.data || shoppingResponse || [];

      const profiles = Array.isArray(profileData) ? profileData : [];
      const shopping = Array.isArray(shoppingData) ? shoppingData : [];

      const userShopping = shopping.filter(
        (item) =>
          item.customerEmail === currentUser.email ||
          item.customerId === currentUser.uid ||
          item.email_id === currentUser.email
      );

      const totalPurchases = userShopping.length;
      const totalPoints = userShopping.reduce(
        (sum, item) => sum + (item.pointsEarned || item.point_given || item.points || 0),
        0
      );
      const totalSpent = userShopping.reduce((sum, item) => {
        const amount = item.amount || item.totalAmount || 0;
        return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
      }, 0);
      const totalSaved = userShopping.reduce(
        (sum, item) => sum + (item.redeem_points|| 0),
        0
      );
     

      setStats({
        totalCustomers: profiles.length,
        totalPurchases,
        totalPoints,
        amountSpent: totalSpent,
        amountSaved: totalSaved,
      });

      // Add 5 mock recent activity items
      const mockRecentActivity = [
        // {
        //   id: 'mock_1',
        //   shopName: 'Coffee Paradise',
        //   businessName: 'Coffee Paradise',
        //   amount: 250,
        //   pointsEarned: 25,
        //   date: new Date().toISOString(),
        //   createdAt: new Date().toISOString(),
        //   transaction_date: new Date().toISOString(),
        //   status: 'Completed',
        //   paymentMethod: 'UPI'
        // },
        {
          id: 'mock_2',
          shopName: 'Fashion Hub',
          businessName: 'Fashion Hub',
          amount: 1500,
          pointsEarned: 150,
          date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          transaction_date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          status: 'Completed',
          paymentMethod: 'Card'
        },
        {
          id: 'mock_3',
          shopName: 'Grocery Store',
          businessName: 'Grocery Store',
          amount: 800,
          pointsEarned: 80,
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          transaction_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Completed',
          paymentMethod: 'Cash'
        },
        {
          id: 'mock_4',
          shopName: 'Electronics World',
          businessName: 'Electronics World',
          amount: 3200,
          pointsEarned: 320,
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          transaction_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Completed',
          paymentMethod: 'Card'
        },
        {
          id: 'mock_5',
          shopName: 'Bookstore Cafe',
          businessName: 'Bookstore Cafe',
          amount: 450,
          pointsEarned: 45,
          date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          transaction_date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Completed',
          paymentMethod: 'UPI'
        }
      ];

      // Combine API data with mock data
      const combinedActivity = [...userShopping, ...mockRecentActivity];
      
      const sortedActivity = combinedActivity
        .sort(
          (a, b) =>
            new Date(b.date || b.createdAt || b.transaction_date || 0) -
            new Date(a.date || a.createdAt || a.transaction_date || 0)
        );
      
      // Store all activity for "View All" feature
      const recentActivityFirst5 = sortedActivity.slice(0, 5);

      setRecentActivity({
        recent: recentActivityFirst5,
        all: sortedActivity
      });
    } catch (error) {
      console.error('Error fetching user stats:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  // Helper: date range filtering
  const filterByDateRange = (items, getDate) => {
    if (!Array.isArray(items)) return [];
    const now = new Date();
    if (cardDateFilter === 'today') {
      const today = now.toDateString();
      return items.filter(it => new Date(getDate(it)).toDateString() === today);
    }
    if (cardDateFilter === 'lastWeek') {
      const sevenAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      return items.filter(it => {
        const d = new Date(getDate(it));
        return d >= sevenAgo && d <= now;
      });
    }
    if (cardDateFilter === 'lastMonth') {
      const thirtyAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
      return items.filter(it => {
        const d = new Date(getDate(it));
        return d >= thirtyAgo && d <= now;
      });
    }
    if (cardDateFilter === 'thisMonth') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return items.filter(it => {
        const d = new Date(getDate(it));
        return d >= start && d <= end;
      });
    }
    if (cardDateFilter === 'custom' && cardCustomDateRange.start && cardCustomDateRange.end) {
      const start = new Date(cardCustomDateRange.start);
      const end = new Date(cardCustomDateRange.end);
      return items.filter(it => {
        const d = new Date(getDate(it));
        return d >= start && d <= end;
      });
    }
    return items;
  };

  const handleMenuAction = (view) => {
    setSelectedCard(null);
    setTableData([]);
    setShowDropdownMenu(false);
    if (view === 'myProfile') { setShowProfileModal(true); return; }
    if (view === 'claimPoints') {
      setShowClaimModal(true);
      return;
    }
    setCurrentView(view);
  };

  const saveProfile = async () => {
    try {
      const firstName = (profileForm.firstName || '').trim();
      const lastName = (profileForm.lastName || '').trim();
      const mobile = (profileForm.mobile || '').trim();
      if (!firstName || !mobile) {
        toast.error('First name and mobile are required');
        return;
      }
      // unique mobile validation
      const res = await AllProfile({});
      const list = Array.isArray(res.extractedData) ? res.extractedData : (res.extractedData?.records || []);
      const mineId = currentUser?.uid;
      const duplicate = (list || []).find(p => (p.mobile === mobile) && (p.profile_id !== mineId));
      if (duplicate) {
        toast.error('Mobile number already in use');
        return;
      }
      setProfileSaving(true);
      await AllProfileUpdate({ profile_id: mineId, firstName, lastName, mobile });
      toast.success('Profile saved');
      setShowProfileModal(false);
    } catch (e) {
      console.error('Profile save failed', e);
      toast.error('Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleCardClick = async (cardType) => {
    setSelectedCard(cardType);
    setCurrentView('cardDetails');
    // reset filters & pagination on open
    setCardDateFilter('all');
    setCardCustomDateRange({ start: '', end: '' });
    setCardCurrentPage(1);

    try {
      setLoading(true);
      let data = [];
const ShopDetails = await fetchNearbyShops({});
const shopData = ShopDetails?.extractedData || ShopDetails || [];
const userShop =(Array.isArray(shopData) ? shopData : []).filter(
  (item) =>
    item.customerEmail === currentUser.email ||
    item.customerId === currentUser.uid ||
    item.email_id === currentUser.email
);
      const shoppingResponse = await AllCustomerShopping({});
      const shoppingData = shoppingResponse?.extractedData || shoppingResponse || [];
      const userShopping = (Array.isArray(shoppingData) ? shoppingData : []).filter(
        (item) =>
          item.customerEmail === currentUser.email ||
          item.customerId === currentUser.uid ||
          item.email_id === currentUser.email
      );

      if (cardType === 'purchases') {
        data = userShopping.map((item) => ({
          id: item.id,
          shop: item.shopName || item.businessName || item.name,
          date: item.date || item.createdAt || item.transaction_date,
          amount: item.amount,
          points: item.pointsEarned || item.point_given || 0,
          type: 'Purchase',
          status: item.status || 'Completed',
        }));
      } else if (cardType === 'points') {
        data = userShopping
          .filter((item) => (item.pointsEarned || item.point_given) > 0)
          .map((item) => ({
            id: item.id,
            shop: item.shopName || item.businessName || item.name,
            date: item.date || item.createdAt || item.transaction_date,
            amount: item.amount,
            points: item.pointsEarned || item.point_given,
            type: 'Points Earned',
          }));
      } else if (cardType === 'amountSpent') {
        data = userShopping.map((item) => ({
          id: item.id,
          shop: item.shopName || item.businessName || item.name,
          date: item.date || item.createdAt || item.transaction_date,
          amount: item.amount || item.totalAmount || 0,
          points: item.pointsEarned || item.point_given || 0,
          status: item.status || 'Completed',
        }));
      } else if (cardType === 'amountSaved') {
        data = userShopping
          .filter((item) => Number(item.redeem_points || 0) > 0)
          .map((item) => ({
            id: item.id,
            shop: item.shopName || item.businessName || item.name,
            date: item.date || item.createdAt || item.transaction_date,
            amount: item.amount || item.totalAmount || 0,
            redeemPoints: Number(item.redeem_points || 0),
            pointsGiven: Number(item.pointsEarned || item.point_given || item.points || 0),
            status: item.status || 'Completed',
          }));
      }

      setTableData(data);
    } 
    catch (error) {
      console.error('Error fetching card details:', error);
      toast.error('Failed to load details');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch {
      toast.error('Failed to logout');
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, onClick, color = 'blue' }) => {
    const colorClasses = {
      blue: { border: 'border-blue-500', bg: 'bg-blue-100', text: 'text-blue-600' },
      green: { border: 'border-green-500', bg: 'bg-green-100', text: 'text-green-600' },
      purple: { border: 'border-purple-500', bg: 'bg-purple-100', text: 'text-purple-600' },
    };
    const colors = colorClasses[color] || colorClasses.blue;

    return (
      <div
        onClick={onClick}
        className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-6 cursor-pointer transform hover:scale-105 border-l-4 ${colors.border}`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.text}`} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`${theme.gradient} shadow-lg`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  Welcome back, {currentUser?.displayName?.split(' ')[0]}!
                </h1>
                <p className="text-blue-100">Manage your rewards and discover new offers</p>
              </div>
            </div>

            <div className="relative dropdown-container">
              <button
                onClick={() => setShowDropdownMenu(!showDropdownMenu)}
                className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Menu className="w-4 h-4" />
                <span>Menu</span>
              </button>

              {showDropdownMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                  <button
                    onClick={() => handleMenuAction('qrCode')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <QrCode className="w-4 h-4" />
                    <span>My QR Code</span>
                  </button>

                  <button
                    onClick={() => handleMenuAction('myProfile')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <User className="w-4 h-4" />
                    <span>My Profile</span>
                  </button>

                  <button
                    onClick={() => handleMenuAction('notifications')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <Bell className="w-4 h-4" />
                    <span>Notifications</span>
                  </button>

                  <button
                    onClick={() => handleMenuAction('findShops')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <MapPin className="w-4 h-4" />
                    <span>Find My Shop</span>
                  </button>

                  <button
                    onClick={() => handleMenuAction('transactions')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <History className="w-4 h-4" />
                    <span>Transaction History</span>
                  </button>

                  <button
                    onClick={() => handleMenuAction('claimPoints')}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <Coins className="w-4 h-4" />
                    <span>Claim Points</span>
                  </button>

                  

                  <div className="border-t my-2"></div>

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}

        
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Cards */}
        {currentView === 'dashboard' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                icon={ShoppingBag}
                title="Total Purchases"
                value={stats.totalPurchases.toLocaleString()}
                subtitle="Your transactions"
                onClick={() => handleCardClick('purchases')}
                color="green"
              />
              <StatCard
                icon={Coins}
                title="Total Points"
                value={stats.totalPoints.toLocaleString()}
                subtitle="Available to redeem"
                onClick={() => handleCardClick('points')}
                color="purple"
              />
              <StatCard
                icon={DollarSign}
                title="Amount Spent"
                value={`₹${Number(stats.amountSpent || 0).toLocaleString()}`}
                subtitle="Total money spent"
                onClick={() => handleCardClick('amountSpent')}
                color="blue"
              />
              <StatCard
                icon={Coins}
                title="Amount Saved"
                value={`₹${Number((stats.amountSaved / 1000).toFixed(0)).toLocaleString()}K`}
                subtitle="Discounts & savings"
                onClick={() => handleCardClick('amountSaved')}
                color="green"
              />
            </div>

            {/* Recent Activity with filters & pagination */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Search by shop or amount..."
                    value={recentSearchTerm}
                    onChange={(e) => { setRecentSearchTerm(e.target.value); setRecentCurrentPage(1); }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="date"
                    value={recentExactDate}
                    onChange={(e) => { setRecentExactDate(e.target.value); setRecentCurrentPage(1); }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    aria-label="Filter by date"
                  />
                </div>
              </div>

              {(() => {
                const all = recentActivity.all || [];
                const term = recentSearchTerm.trim().toLowerCase();
                const now = new Date();
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

                const inDateRange = (d) => {
                  const dt = new Date(d);
                  // If exact date is chosen, prefer exact day match
                  if (recentExactDate) {
                    const selected = new Date(recentExactDate);
                    return dt.toDateString() === selected.toDateString();
                  }
                  if (recentDateFilter === 'today') {
                    return dt.toDateString() === now.toDateString();
                  }
                  if (recentDateFilter === 'thisMonth') {
                    return dt >= startOfMonth && dt <= endOfMonth;
                  }
                  if (recentDateFilter === 'custom' && recentCustomDateRange.start && recentCustomDateRange.end) {
                    const s = new Date(recentCustomDateRange.start);
                    const e = new Date(recentCustomDateRange.end);
                    return dt >= s && dt <= e;
                  }
                  return true;
                };

                const filtered = all.filter((a) => {
                  const name = (a.shopName || a.businessName || 'Purchase').toLowerCase();
                  const amountStr = String(a.amount || 0);
                  const dateVal = a.date || a.createdAt || a.transaction_date;
                  const matchesTerm = !term || name.includes(term) || amountStr.includes(term);
                  const matchesDate = inDateRange(dateVal);
                  return matchesTerm && matchesDate;
                });

                const pageCount = Math.max(1, Math.ceil(filtered.length / recentItemsPerPage));
                const current = Math.min(recentCurrentPage, pageCount);
                const start = (current - 1) * recentItemsPerPage;
                const pageItems = filtered.slice(start, start + recentItemsPerPage);

                return (
                  <>
                    <div className="space-y-4">
                      {pageItems.length > 0 ? (
                        pageItems.map((activity, index) => {
                          const statusLower = String(activity.status || '').toLowerCase();
                          const isOtpSent = statusLower === 'otp_sent';
                          const isPendingRedeem = (statusLower === 'pending_customer') && (activity.pending_redeem_points || activity.redeemPoints);
                          const redeemPts = Number(activity.pending_redeem_points || activity.redeemPoints || 0);
                          return (
                            <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isOtpSent ? 'bg-indigo-100' : (isPendingRedeem ? 'bg-yellow-100' : 'bg-green-100')}`}>
                                  <Coins className={`w-5 h-5 ${isOtpSent ? 'text-indigo-600' : (isPendingRedeem ? 'text-yellow-600' : 'text-green-600')}`} />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {isOtpSent ? 'OTP sent for redemption' : (isPendingRedeem ? 'Pending Redemption' : (activity.shopName || activity.businessName || 'Purchase'))}
                                  </p>
                                  {isOtpSent ? (
                                    <p className="text-sm text-gray-500">OTP for redemption: <span className="font-semibold text-gray-900">{activity.otp_code || '----'}</span></p>
                                  ) : isPendingRedeem ? (
                                    <p className="text-sm text-gray-500">Request to redeem {redeemPts} pts (₹{redeemPts * 10})</p>
                                  ) : (
                                    <p className="text-sm text-gray-500">Amount: ₹{activity.amount || 0}</p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                {isOtpSent ? (
                                  <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-700 text-xs">OTP Sent</span>
                                ) : isPendingRedeem ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      className="px-3 py-1 rounded bg-green-600 text-white text-xs"
                                      onClick={async () => {
                                        try {
                                          // Customer consents only; do not deduct here. Mark pending record approved.
                                          const recId = activity._id || activity.id;
                                          if (recId) {
                                            await updateCustomerTransaction(recId, { status: 'approved', approvedDate: new Date().toISOString() });
                                          }
                                          toast.success('Redemption approved. Merchant will submit.');
                                          fetchUserStats();
                                        } catch (e) {
                                          console.error('Approve redemption failed', e);
                                          toast.error('Failed to approve redemption');
                                        }
                                      }}
                                    >
                                      Approve
                                    </button>
                                    <button
                                      className="px-3 py-1 rounded bg-gray-200 text-gray-800 text-xs"
                                      onClick={async () => {
                                        try {
                                          const recId = activity._id || activity.id;
                                          if (recId) {
                                            await updateCustomerTransaction(recId, { status: 'rejected', rejectedDate: new Date().toISOString() });
                                          }
                                          toast.success('Redemption rejected');
                                          fetchUserStats();
                                        } catch (e) {
                                          console.error('Reject redemption failed', e);
                                          toast.error('Failed to reject redemption');
                                        }
                                      }}
                                    >
                                      Reject
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <p className="font-semibold text-green-600">+{activity.pointsEarned || activity.point_given || 0} pts</p>
                                    <p className="text-xs text-gray-500">{new Date(activity.date || activity.createdAt || activity.transaction_date).toLocaleDateString()}</p>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8">
                          <Coins className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No activity found</p>
                        </div>
                      )}
                    </div>

                    {/* Pagination */}
                    {pageCount > 1 && (
                      <div className="flex items-center justify-between border-t pt-4 mt-4">
                        <p className="text-gray-600">
                          Showing {filtered.length === 0 ? 0 : start + 1}-{Math.min(start + recentItemsPerPage, filtered.length)} of {filtered.length} activities
                        </p>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setRecentCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={current === 1}
                            className="flex items-center space-x-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            <span>Previous</span>
                          </button>
                          {Array.from({ length: pageCount }, (_, i) => i + 1).map(page => (
                            <button
                              key={page}
                              onClick={() => setRecentCurrentPage(page)}
                              className={`px-3 py-2 rounded-lg ${current === page ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            onClick={() => setRecentCurrentPage(prev => Math.min(prev + 1, pageCount))}
                            disabled={current === pageCount}
                            className="flex items-center space-x-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                          >
                            <span>Next</span>
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="text-center py-4">
                      <button
                        onClick={() => handleMenuAction('allActivity')}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
                      >
                        View All Activity
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </>
        )}

        {/* Card Details Table */}
        {currentView === 'cardDetails' && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                {selectedCard === 'purchases' && 'Your Purchase History'}
                {selectedCard === 'points' && 'Points  History'}
                {selectedCard === 'amountSpent' && 'Amount Spent '}
                {selectedCard === 'amountSaved' && 'Amount Saved '}
              </h2>
              <button
                onClick={() => handleMenuAction('dashboard')}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Back</span>
              </button>
            </div>
            {/* Card search + date controls */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <input
                type="text"
                placeholder="Search by shop or amount..."
                value={cardSearchTerm}
                onChange={(e) => { setCardSearchTerm(e.target.value); setCardCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {/* <input
                type="date"
                value={cardExactDate}
                onChange={(e) => { setCardExactDate(e.target.value); setCardCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                aria-label="Filter by exact date"
              /> */}
              <select
                value={cardDateFilter}
                onChange={(e) => { setCardDateFilter(e.target.value); setCardCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="lastWeek">Last 7 Days</option>
                <option value="lastMonth">Last 30 Days</option>
                <option value="custom">Custom</option>
              </select>
              {cardDateFilter === 'custom' && (
                <>
                  <input
                    type="date"
                    value={cardCustomDateRange.start}
                    onChange={(e) => { setCardCustomDateRange(prev => ({ ...prev, start: e.target.value })); setCardCurrentPage(1); }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="date"
                    value={cardCustomDateRange.end}
                    onChange={(e) => { setCardCustomDateRange(prev => ({ ...prev, end: e.target.value })); setCardCurrentPage(1); }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </>
              )}
            </div>
            {(() => {
             const columns =
             selectedCard === 'purchases'
               ? [
                 
                  { key: 'date', title: 'Date', align: 'left', render: (v) => new Date(v).toLocaleDateString() },
                  { key: 'shop', title: 'Shop Name', align: 'left' },
                  { key: 'amount', title: 'Amount', align: 'right', type: 'currency', headerClassName: 'text-right', className: 'text-right' },
                  { key: 'points', title: 'Points', align: 'right', type: 'number', headerClassName: 'text-right', className: 'text-right' },
                ]
              : selectedCard === 'points'
              ? [
                { key: 'date', title: 'Date', align: 'left', render: (v) => new Date(v).toLocaleDateString() },
                  { key: 'shop', title: 'Shop Name', align: 'left' },
                  { key: 'amount', title: 'Amount', align: 'right', type: 'currency', headerClassName: 'text-right', className: 'text-right' },
                  { key: 'points', title: 'Points', align: 'right', type: 'number', headerClassName: 'text-right', className: 'text-right' },
                ]
              : selectedCard === 'amountSpent'
              ? [
                  
                  { key: 'date', title: 'Date', align: 'left', render: (v) => new Date(v).toLocaleDateString() },
                  { key: 'shop', title: 'Shop Name', align: 'left' },
                  { key: 'amount', title: 'Amount', align: 'right', type: 'currency', headerClassName: 'text-right', className: 'text-right' },
                ]
              : selectedCard === 'amountSaved'
              ? [
                  { key: 'date', title: 'Date', align: 'left', render: (v) => new Date(v).toLocaleDateString() },
                  { key: 'shop', title: 'Shop Name', align: 'left' },
                  { key: 'amount', title: 'Amount', align: 'right', type: 'currency', headerClassName: 'text-right', className: 'text-right' },
                  { key: 'redeemPoints', title: 'Redeem Points', align: 'right', type: 'number', headerClassName: 'text-right', className: 'text-right' },
                  { key: 'pointsGiven', title: 'Points Given', align: 'right', type: 'number', headerClassName: 'text-right', className: 'text-right' },
                ]
              : [];
           

              const title =
                selectedCard === 'purchases'
                  ? 'Purchase History'
                  : selectedCard === 'points'
                  ? 'Points History'
                  : selectedCard === 'amountSpent'
                  ? 'Amount Spent '
                  : selectedCard === 'amountSaved'
                  ? 'Amount Saved '
                  : '';

              // Apply date + search filtering and pagination to tableData
              const dateKey = (row) => row.date || row.createdAt || row.transaction_date;
              const inExactDate = (row) => {
                if (!cardExactDate) return true;
                const dt = new Date(dateKey(row));
                const sel = new Date(cardExactDate);
                return dt.toDateString() === sel.toDateString();
              };
              const byRange = cardExactDate
                ? tableData // if exact date chosen, skip range filter
                : filterByDateRange(tableData, dateKey);
              const byDate = byRange.filter(inExactDate);

              const term = cardSearchTerm.trim().toLowerCase();
              const bySearch = term
                ? byDate.filter((row) => {
                    const name = String(row.shop || row.businessName || row.name || '').toLowerCase();
                    const amount = row.amount != null ? String(row.amount) : '';
                    const points = row.points != null ? String(row.points) : '';
                    const savings = row.savings != null ? String(row.savings) : '';
                    return (
                      name.includes(term) ||
                      amount.includes(term) ||
                      points.includes(term) ||
                      savings.includes(term)
                    );
                  })
                : byDate;

              const filtered = bySearch;
              const pageCount = Math.max(1, Math.ceil(filtered.length / cardItemsPerPage));
              const current = Math.min(cardCurrentPage, pageCount);
              const start = (current - 1) * cardItemsPerPage;
              const paged = filtered.slice(start, start + cardItemsPerPage);

              return (
                <>
                  <DataTable
                    data={paged}
                    columns={columns}
                    title={title}
                    showHeader={false}
                    showSearch={false}
                  />
                  {/* Pagination */}
                  {pageCount > 1 && (
                    <div className="flex items-center justify-between border-t pt-4 mt-4">
                      <p className="text-gray-600">
                        Showing {filtered.length === 0 ? 0 : start + 1}-{Math.min(start + cardItemsPerPage, filtered.length)} of {filtered.length} records
                      </p>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCardCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={current === 1}
                          className="flex items-center space-x-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          <span>Previous</span>
                        </button>
                        {Array.from({ length: pageCount }, (_, i) => i + 1).map(page => (
                          <button
                            key={page}
                            onClick={() => setCardCurrentPage(page)}
                            className={`px-3 py-2 rounded-lg ${current === page ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          onClick={() => setCardCurrentPage(prev => Math.min(prev + 1, pageCount))}
                          disabled={current === pageCount}
                          className="flex items-center space-x-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          <span>Next</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Find My Shop */}
        {currentView === 'findShops' && <FindMyShop onBack={() => handleMenuAction('dashboard')} />}

        {/* Transaction History */}
        {currentView === 'transactions' && (
          <TransactionHistory onBack={() => handleMenuAction('dashboard')} />
        )}

        {/* Profile Modal */}
        {showProfileModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
              <div className="p-6 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold">Complete Your Profile</h3>
                <button onClick={() => setShowProfileModal(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5"/></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">First Name</label>
                  <input value={profileForm.firstName} onChange={(e)=>setProfileForm(p=>({...p, firstName:e.target.value}))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"/>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Last Name</label>
                  <input value={profileForm.lastName} onChange={(e)=>setProfileForm(p=>({...p, lastName:e.target.value}))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"/>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Mobile</label>
                  <input value={profileForm.mobile} onChange={(e)=>setProfileForm(p=>({...p, mobile:e.target.value.replace(/[^0-9+]/g,'')}))} placeholder="10-digit mobile" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"/>
                  <p className="text-xs text-gray-500 mt-1">Mobile number must be unique.</p>
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-2">
                <button onClick={()=>setShowProfileModal(false)} className="px-4 py-2 rounded-lg border">Cancel</button>
                <button onClick={saveProfile} disabled={profileSaving} className={`px-4 py-2 rounded-lg text-white ${profileSaving?'bg-gray-400':'bg-blue-600 hover:bg-blue-700'}`}>{profileSaving?'Saving...':'Save'}</button>
              </div>
            </div>
          </div>
        )}

        {/* All Activity View */}
        {currentView === 'allActivity' && (
          <div className="bg-white rounded-xl shadow-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">All Activity</h2>
                <p className="text-gray-600 mt-1">Complete history of your transactions and points</p>
              </div>
              <button
                onClick={() => handleMenuAction('dashboard')}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Back</span>
              </button>
            </div>
            
            <div className="p-6">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <input
                  type="text"
                  placeholder="Search by shop name or amount..."
                  value={allSearchTerm}
                  onChange={(e) => { setAllSearchTerm(e.target.value); setAllActivityCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="number"
                  placeholder="Min Amount"
                  value={allAmountMin}
                  onChange={(e) => { setAllAmountMin(e.target.value); setAllActivityCurrentPage(1); }}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max Amount"
                  value={allAmountMax}
                  onChange={(e) => { setAllAmountMax(e.target.value); setAllActivityCurrentPage(1); }}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={allDateFilter}
                  onChange={(e) => { setAllDateFilter(e.target.value); setAllActivityCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="thisMonth">This Month</option>
                  <option value="custom">Custom</option>
                </select>
                {allDateFilter === 'custom' && (
                  <>
                    <input
                      type="date"
                      value={allCustomDateRange.start}
                      onChange={(e) => setAllCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="date"
                      value={allCustomDateRange.end}
                      onChange={(e) => setAllCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </>
                )}
              </div>

              {(() => {
                const all = recentActivity.all || [];
                const term = allSearchTerm.trim().toLowerCase();
                const min = allAmountMin !== '' ? parseFloat(allAmountMin) : null;
                const max = allAmountMax !== '' ? parseFloat(allAmountMax) : null;
                const now = new Date();
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

                const inDateRange = (d) => {
                  const dt = new Date(d);
                  // If exact date selected, prefer exact match
                  if (allExactDate) {
                    const selected = new Date(allExactDate);
                    return dt.toDateString() === selected.toDateString();
                  }
                  if (allDateFilter === 'today') return dt.toDateString() === now.toDateString();
                  if (allDateFilter === 'thisMonth') return dt >= startOfMonth && dt <= endOfMonth;
                  if (allDateFilter === 'custom' && allCustomDateRange.start && allCustomDateRange.end) {
                    const s = new Date(allCustomDateRange.start);
                    const e = new Date(allCustomDateRange.end);
                    return dt >= s && dt <= e;
                  }
                  return true;
                };

                const filtered = all.filter((a) => {
                  const name = (a.shopName || a.businessName || 'Purchase').toLowerCase();
                  const amount = parseFloat(a.amount || 0) || 0;
                  const dateVal = a.date || a.createdAt || a.transaction_date;
                  const matchTerm = !term || name.includes(term) || String(amount).includes(term);
                  const matchAmt = (min === null || amount >= min) && (max === null || amount <= max);
                  const matchDate = inDateRange(dateVal);
                  return matchTerm && matchAmt && matchDate;
                });

                const pageCount = Math.max(1, Math.ceil(filtered.length / allActivityItemsPerPage));
                const current = Math.min(allActivityCurrentPage, pageCount);
                const start = (current - 1) * allActivityItemsPerPage;
                const pageItems = filtered.slice(start, start + allActivityItemsPerPage);

                if (all.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Coins className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Found</h3>
                      <p className="text-gray-600">Start shopping to see your transaction history here!</p>
                    </div>
                  );
                }

                return (
                  <>
                    <div className="space-y-4 mb-6">
                      {pageItems.map((activity, index) => (
                        <div key={index} className="flex items-center justify-between py-4 px-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                              <Coins className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{activity.shopName || activity.businessName || 'Purchase'}</p>
                              <p className="text-sm text-gray-600">Amount: ₹{activity.amount || 0}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600 text-lg">+{activity.pointsEarned || activity.point_given || 0} pts</p>
                            <p className="text-sm text-gray-500">{new Date(activity.date || activity.createdAt || activity.transaction_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {filtered.length > 0 && (
                      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                        <p className="text-gray-600">
                          Showing {filtered.length === 0 ? 0 : start + 1}-{Math.min(start + allActivityItemsPerPage, filtered.length)} of {filtered.length} activities
                        </p>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setAllActivityCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={current === 1}
                            className="flex items-center space-x-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            <span>Previous</span>
                          </button>
                          {Array.from({ length: pageCount }, (_, i) => i + 1).map(page => (
                            <button
                              key={page}
                              onClick={() => setAllActivityCurrentPage(page)}
                              className={`px-4 py-2 rounded-lg ${current === page ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            onClick={() => setAllActivityCurrentPage(prev => Math.min(prev + 1, pageCount))}
                            disabled={current === pageCount}
                            className="flex items-center space-x-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span>Next</span>
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Notifications View */}
        {currentView === 'notifications' && (
          <div className="bg-white rounded-xl shadow-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">Notifications</h2>
                <p className="text-gray-600 mt-1">Stay updated with your latest offers and rewards</p>
              </div>
              <button
                onClick={() => handleMenuAction('dashboard')}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Back</span>
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {/* Sample notifications */}
                <div className="flex items-start space-x-4 p-4 border border-blue-200 bg-blue-50 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Bell className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Welcome Bonus!</h4>
                    <p className="text-gray-600 text-sm mt-1">You've earned 100 bonus points for joining our rewards program.</p>
                    <p className="text-xs text-gray-500 mt-2">2 hours ago</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 border border-green-200 bg-green-50 rounded-lg">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Coins className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Points Earned</h4>
                    <p className="text-gray-600 text-sm mt-1">You earned 25 points from your recent purchase at ABC Store.</p>
                    <p className="text-xs text-gray-500 mt-2">1 day ago</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 border border-purple-200 bg-purple-50 rounded-lg">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <QrCode className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Special Offer Available</h4>
                    <p className="text-gray-600 text-sm mt-1">Get 20% off on your next purchase. Show your QR code at checkout.</p>
                    <p className="text-xs text-gray-500 mt-2">3 days ago</p>
                  </div>
                </div>
              </div>
              
              <div className="text-center py-8 border-t border-gray-200 mt-6">
                <p className="text-gray-500">You're all caught up!</p>
                <p className="text-sm text-gray-400 mt-1">Check back later for new notifications.</p>
              </div>
            </div>
          </div>
        )}

        {/* Claim Points Modal */}
        {showClaimModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Claim Points</h3>
                <button
                  onClick={() => setShowClaimModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Available Points</span>
                  <span className="font-semibold">{Number(stats.totalPoints || 0).toLocaleString()}</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shop Name</label>
                  <input
                    type="text"
                    value={claimShopName}
                    onChange={(e) => { setClaimShopName(e.target.value); setClaimOptionsVisible(true); }}
                    placeholder="e.g., Coffee Paradise"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {/* Suggestions */}
                  {showClaimModal && claimOptionsVisible && (
                    <div className="mt-1 border border-gray-200 rounded-lg bg-white max-h-52 overflow-auto shadow">
                      {claimOptionsLoading ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                      ) : (
                        (getFilteredClaimOptions()).map((opt, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => applyClaimSelection(opt)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                          >
                            <span className="text-gray-800">{opt.shopName || 'Shop'}</span>
                            <span className="text-xs text-gray-500">{opt.email}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Email (optional)</label>
                  <input
                    type="email"
                    value={claimBusinessEmail}
                    onChange={(e) => { setClaimBusinessEmail(e.target.value); setClaimOptionsVisible(true); }}
                    placeholder="business@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {/* Suggestions (same list) */}
                  {showClaimModal && claimOptionsVisible && (
                    <div className="mt-1 border border-gray-200 rounded-lg bg-white max-h-52 overflow-auto shadow">
                      {claimOptionsLoading ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                      ) : (
                        (getFilteredClaimOptions()).map((opt, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => applyClaimSelection(opt)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                          >
                            <span className="text-gray-800">{opt.shopName || 'Shop'}</span>
                            <span className="text-xs text-gray-500">{opt.email}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">If empty, we will try to find the business by shop name.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={claimAmount}
                    onChange={(e) => setClaimAmount(e.target.value)}
                    placeholder="Enter bill amount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {usePoints && Number(claimAmount) > 0 && (
                    <p className="text-xs text-gray-600 mt-1">
                      Will deduct <span className="font-semibold">{Math.floor(Number(claimAmount)/10)}</span> points (₹1000 = 100 points)
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload Bill</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setClaimBillFile(e.target.files?.[0] || null)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Image or PDF up to ~2MB recommended.</p>
                </div>
                {/* <div className="flex items-center gap-2 pt-2">
                  <input id="usePointsToggle" type="checkbox" className="h-4 w-4" checked={usePoints} onChange={(e)=>setUsePoints(e.target.checked)} />
                  <label htmlFor="usePointsToggle" className="text-sm text-gray-700">Use points for this purchase (deduct based on amount)</label>
                </div> */}
                <div className="pt-2">
                  <button
                    disabled={claimSubmitting}
                    onClick={async () => {
                      try {
                        if (!currentUser?.email) {
                          toast.error('You must be logged in.');
                          return;
                        }
                        if (!claimShopName || !claimAmount || Number(claimAmount) <= 0) {
                          toast.error('Enter valid shop name and amount.');
                          return;
                        }
                        setClaimSubmitting(true);
                        // Try to resolve business by email or by shop name
                        let businessEmail = claimBusinessEmail?.trim();
                        if (!businessEmail) {
                          try {
                            const resp = await fetchNearbyShops({});
                            const profiles = Array.isArray(resp?.extractedData) ? resp.extractedData : (resp?.extractedData?.records || []);
                            const normalized = String(claimShopName).trim().toLowerCase();
                            const match = (profiles || []).find((p) => {
                              const shopArr = Array.isArray(p.shopDetails) ? p.shopDetails : [];
                              const shopName = shopArr?.[0]?.shopName || '';
                              return shopName.trim().toLowerCase() === normalized;
                            });
                            if (match?.email) {
                              businessEmail = match.email;
                            }
                          } catch (e) {
                            // ignore lookup errors
                          }
                        }

                        // If using points, create a redemption transaction first
                        if (usePoints) {
                          const amt = Number(claimAmount);
                          const pts = Math.floor(amt / 10);
                          if (pts <= 0) { toast.error('Amount too low to redeem points.'); setClaimSubmitting(false); return; }
                          if (pts > Number(stats.totalPoints || 0)) { toast.error('Insufficient points.'); setClaimSubmitting(false); return; }
                          const tx = {
                            customerEmail: currentUser.email,
                            customerId: currentUser.uid,
                            customerName: currentUser.displayName,
                            businessEmail: businessEmail || undefined,
                            businessName: claimShopName,
                            amount: amt,
                            pointsEarned: -pts,
                            type: 'Redemption',
                            status: 'completed',
                            date: new Date().toISOString(),
                          };
                          await createCustomerTransaction(tx);
                        }

                        if (!businessEmail) {
                          toast.error('Could not resolve business email. Please enter it.');
                          return;
                        }

                        let billBase64 = '';
                        if (claimBillFile) {
                          billBase64 = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(String(reader.result));
                            reader.onerror = reject;
                            reader.readAsDataURL(claimBillFile);
                          });
                        }

                        const payload = {
                          businessEmail,
                          businessName: claimShopName,
                          customerEmail: currentUser.email,
                          customerId: currentUser.uid,
                          customerName: currentUser.displayName,
                          amount: Number(claimAmount),
                          billImage: billBase64, // dataURL string
                          status: 'pending',
                          requestDate: new Date().toISOString(),
                          usePoints: !!usePoints,
                          redeemPoints: usePoints ? Math.floor(Number(claimAmount)/10) : 0,
                        };

                        await createClaimRequest(payload);
                        toast.success(usePoints ? 'Claim submitted and points redeemed' : 'Claim request sent to business');
                        setShowClaimModal(false);
                        setClaimShopName('');
                        setClaimBusinessEmail('');
                        setClaimAmount('');
                        setClaimBillFile(null);
                        setUsePoints(false);
                        fetchUserStats();
                      } catch (err) {
                        console.error('Claim submit failed', err);
                        toast.error('Failed to submit claim');
                      } finally {
                        setClaimSubmitting(false);
                      }
                    }}
                    className={`w-full ${claimSubmitting ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 py-2 rounded-lg transition-colors`}
                  >
                    {claimSubmitting ? 'Submitting...' : (usePoints ? 'Submit & Redeem' : 'Submit Claim')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* QR Code */}
        {currentView === 'qrCode' && (
          <CustomerQRCode onClose={() => handleMenuAction('dashboard')} customerData={stats} />
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
